import { Box, Flex, Grid, Skeleton, Text } from '@radix-ui/themes';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSliderInput } from 'components/common/form/slider';
import { Blinker } from 'components/machine/camera-stream-viewer/blinker';
import { AuthContext } from 'contexts/auth.context';
import { format } from 'date-fns-tz';
import { LOCAL_TIMEZONE } from 'enums/env';
import { CAMERA_OPTIONS } from 'enums/streaming';
import { t } from 'i18next';
import { CameraSource } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useMemo } from 'react';
import { StreamingContext } from './context';
import './index.scss';

const COMPONENT_NAME = 'MachineCameraStreamViewer';

const TOGGLE_PLAY_KEY = 'Space';

interface IProps {
  machineID: string;
  hideSource?: boolean;
}

const AdminInputs = ({ hideSource }: { hideSource: boolean }) => {
  const { source, refreshRate, setSource, setRefreshRate } =
    useContext(StreamingContext);

  const columns = hideSource ? '1' : '2';

  return (
    <Grid columns={columns} gap={RADIX.FLEX.GAP.MD}>
      {!hideSource && (
        <CommonSelectInput
          id="ms-inspector-source"
          name="source"
          label="Source"
          labelIcon={<SuperAdminIcon />}
          options={CAMERA_OPTIONS}
          value={source}
          onChange={(v) => setSource(v as CameraSource)}
        />
      )}
      <CommonSliderInput
        id="ms-inspector-refresh-rate-slider"
        name="refresh-rate"
        label={`Refresh Rate (${refreshRate} / sec)`}
        labelIcon={<SuperAdminIcon />}
        value={refreshRate}
        min={1}
        max={48}
        step={1}
        onChange={(v) => setRefreshRate(+v)}
        stepperLabel="Refresh Rate"
      />
    </Grid>
  );
};

export const MachineCameraStreamViewer = (props: IProps) => {
  const { featureFlags } = useContext(AuthContext);
  const { lastFrame, togglePaused } = useContext(StreamingContext);

  // toggle playback w/ space
  useEffect(() => {
    const callback = (event: KeyboardEvent) => {
      if (![TOGGLE_PLAY_KEY].includes(event.code)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.repeat) {
        return;
      }

      switch (event.code) {
        case TOGGLE_PLAY_KEY: {
          togglePaused();
          return;
        }

        default: {
          return;
        }
      }
    };

    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
  }, []);

  // update the src of the <img> tag from buffer of last frame
  const imgSrc = useMemo(() => {
    if (!lastFrame?.buffer) {
      return;
    }

    const buffer = new Uint8Array(lastFrame.buffer);
    const blob = new Blob([buffer.buffer]);

    return window.URL.createObjectURL(blob);
  }, [lastFrame]);

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD} className="CameraStream">
        {featureFlags.controls.cameraSources && (
          <AdminInputs hideSource={!!props.hideSource} />
        )}
        <Skeleton height="300px" loading={!imgSrc}>
          <Box className="sizer">
            <Blinker />

            <img
              src={imgSrc}
              className="camera-feed-wide select-none"
              alt={`${lastFrame?.source ?? 'unknown'} @ ${
                lastFrame?.timestamp ?? 'unknown'
              }`}
            />

            {lastFrame?.timestamp && (
              <div className="timestamp">
                <Text size={RADIX.TEXT.SIZE.XS}>
                  {t('common.last-updated')}:{' '}
                  {format(
                    new Date(lastFrame.timestamp),
                    'yyyy-MM-dd @ hh:mm:ss a',
                    { timeZone: LOCAL_TIMEZONE }
                  )}
                </Text>
              </div>
            )}
          </Box>
        </Skeleton>
      </Flex>
    </ErrorBoundary>
  );
};
